import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  InteractionStatus,
  AccountInfo,
  SilentRequest
} from '@azure/msal-browser';
import { 
  useMsal,
  useIsAuthenticated,
  useAccount
} from '@azure/msal-react';
import { loginRequest, validateMorganMorganUser, AUTH_SCOPES } from './authConfig';

interface UserInfo {
  oid: string;
  email: string;
  name: string;
  tenantId: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthorized: boolean;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  displayName: string;
  email?: string;
}

interface AuthProviderProps {
  children: ReactNode;
}


const AuthContext = createContext<AuthContextType | null>(null);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  const getUserInfo = (account: AccountInfo | null): UserInfo | null => {
    if (!account) return null;
    return {
      oid: account.localAccountId || '',
      email: account.username || '',
      name: account.name || account.username || '',
      tenantId: account.tenantId || '',
    };
  };

  const login = async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getAccessToken = async (): Promise<string> => {
    if (!account) {
      throw new Error('No authenticated account');
    }

    try {
      const activeAccount = instance.getActiveAccount() || account;
      const idTokenClaims = activeAccount.idTokenClaims;
      
      if (!idTokenClaims || !activeAccount.idToken) {
        throw new Error('No ID token available');
      }

      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      const tokenExpiration = idTokenClaims.exp;
      
      if (!tokenExpiration) {
        throw new Error('Token has no expiration time');
      }
      
      const timeUntilExpiry = tokenExpiration - now;

      if (timeUntilExpiry < 300) {
        try {
          const silentRequest: SilentRequest = {
            scopes: AUTH_SCOPES,
            account: activeAccount,
          };
          
          const response = await instance.acquireTokenSilent(silentRequest);
          
          if (response.idToken) {
            return response.idToken;
          } else {
            throw new Error('Silent refresh returned no token');
          }
          
        } catch (silentError) {
          console.warn('Silent token refresh failed:', silentError);
          await instance.loginRedirect(loginRequest);
          throw new Error('Authentication expired - redirecting to login');
        }
      }
      return activeAccount.idToken;
      
    } catch (error) {
      console.error('Error getting token:', error);
      throw error;
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (isAuthenticated && account && inProgress === InteractionStatus.None) {
        setLoading(true);
        setError(null);

        try {
          const isMorganUser = validateMorganMorganUser(account);
          
          if (!isMorganUser) {
            setError('Access denied. This application is restricted to Morgan & Morgan employees.');
            setIsAuthorized(false);
            await logout();
            return;
          }

          // Set user info and authorization
          const userInfo = getUserInfo(account);
          setUser(userInfo);
          setIsAuthorized(true);
          
        } catch (error) {
          console.error('Error loading user data:', error);
          setError('Failed to load user data');
          setIsAuthorized(false);
        } finally {
          setLoading(false);
        }
      } else if (!isAuthenticated) {
        setUser(null);
        setIsAuthorized(false);
        setLoading(false);
        setError(null);
      }
    };

    loadUserData();
  }, [isAuthenticated, account, inProgress, instance]);

  useEffect(() => {
    if (account?.idTokenClaims?.exp) {
      setTokenExpiresAt(account.idTokenClaims.exp);
    } else {
      setTokenExpiresAt(null);
    }
  }, [account]);

  // Set up proactive token refresh timer
  useEffect(() => {
    if (!tokenExpiresAt || !isAuthenticated) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = tokenExpiresAt - now;
    const refreshBuffer = 600; // 10 minutes

    if (timeUntilExpiry > refreshBuffer) {
      const refreshTimer = setTimeout(async () => {
        try {
          await getAccessToken();
          
          // Update token expiration time after successful refresh
          const activeAccount = instance.getActiveAccount() || account;
          if (activeAccount?.idTokenClaims?.exp) {
            setTokenExpiresAt(activeAccount.idTokenClaims.exp);
          }
        } catch (error) {
          console.warn('Proactive token refresh failed:', error);
        }
      }, (timeUntilExpiry - refreshBuffer) * 1000);

      return () => {
        clearTimeout(refreshTimer);
      };
    }
  }, [tokenExpiresAt, isAuthenticated, instance, account]);

  const value = {
    isAuthenticated: isAuthenticated && isAuthorized,
    isAuthorized,
    user,
    loading,
    error,
    login,
    logout,
    getAccessToken,
    displayName: user?.name || 'User',
    email: user?.email,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const AppAuthProvider = ({ children }: AuthProviderProps) => {
  return (
    <AuthProvider>
      {children}
    </AuthProvider>
  );
};
